import { useEffect, useState, type ReactNode } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import {
  PIXELS_PER_MINUTE,
  buildSlots,
  isSameDay,
  minutesToTime,
  positionYFor,
  timeToMinutes,
} from "@/lib/agenda-time";
import { resolveCardColor } from "@/lib/agenda-colors";
import { layoutAppointments, type LaidOut } from "@/lib/agenda-overlap";
import { CurrentTimeIndicator } from "./CurrentTimeIndicator";
import { AppointmentCard } from "./AppointmentCard";
import { DroppableSlot } from "./DroppableSlot";
import { ALTURA_DA_LINHA_DA_FAIXA, alturaDaFaixa } from "./faixa-de-prazos";

type DayAppointment = {
  id: string;
  patientName: string;
  patientPhone?: string | null;
  procedure: string;
  date: Date | string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  color?: string | null;
  dentistId?: string | null;
  dentistName?: string | null;
  notes?: string | null;
  createdAt?: Date | string;
};

type Props = {
  date: Date;
  /** Vista de dia: o card ocupa a largura toda, então o detalhe abre embaixo (do lado não cabe). */
  diaUnico?: boolean;
  startHour: string;
  endHour: string;
  slotDurationMinutes: number;
  gridLines: "off" | "subtle" | "strong";
  lunchBreak?: { start: string; end: string } | null;
  showNowLine: boolean;
  nowLineVariant: "full" | "ghost";
  nowLineStyle?: "solid" | "dashed";
  nowLineColor?: string;
  nowLineHighlightToday?: boolean;
  isWeekend?: boolean;
  isInactive?: boolean;
  /** Appointments crus — DayColumn faz layout. Mutuamente exclusivo com laidOutAppointments. */
  appointments?: DayAppointment[];
  /** Appointments ja laid-out (pra drag em curso — WeekView pre-calcula). */
  laidOutAppointments?: LaidOut<DayAppointment>[];
  cardStyle?: AgendaSettings["cardStyle"];
  showDentistInCard?: boolean;
  dentistSettings?: AgendaSettings["dentistSettings"];
  showTimeInCard?: boolean;
  onAppointmentEdit?: (id: string) => void;
  onAppointmentStatusChange?: (id: string, status: AppointmentStatus) => void;
  onAppointmentIniciarAtendimento?: (id: string) => void;
  onAppointmentEncerrarAtendimento?: (id: string) => void;
  atendimentoPendente?: boolean;
  onAppointmentDelete?: (id: string) => void;
  onSlotDoubleClick?: (date: Date, startTime: string) => void;
  onAppointmentResize?: (id: string, newStart: string, newEnd: string) => void;
  onHover?: (
    range:
      | {
          date: Date;
          start: string;
          end: string;
          skipSlotHighlight?: boolean;
          leftFrac?: number;
          widthFrac?: number;
        }
      | null,
  ) => void;
  /** Range de slots a destacar. Só aplica se a data bate. */
  highlightRange?: {
    start: string;
    end: string;
    leftFrac?: number;
    widthFrac?: number;
  } | null;
  /** Compact header — usa dayName abreviado ("Seg") quando há muitas colunas. */
  compact?: boolean;
  /** ID do appointment sendo arrastado (pra origem aplicar opacity de fade). */
  draggingId?: string | null;
  /** Pedidos de prótese com prazo nesse dia — faixa "dia inteiro" abaixo do cabeçalho, sem
   *  posição por horário (ProsthesisOrder.deadline não tem hora). */
  deadlineOrders?: Array<{ id: string; patientName: string; overdue: boolean }>;
  /** Linhas da faixa de prazos em TODAS as colunas (a do dia com mais pedidos): mesma altura em todas. */
  linhasDaFaixa?: number;
  onDeadlineOrderClick?: (id: string) => void;
  /** Agenda por sala: a sala desta coluna (SEM_SALA = "Sem sala"), levada no id do slot pro arraste. */
  sala?: string;
  /** Cabeçalho no lugar do dia (agenda por sala: nome e cor da sala). */
  cabecalho?: ReactNode;
};

/**
 * Coluna de um dia. Renderiza fundo com linhas de slot + bloqueio de almoço
 * + linha do "agora" (se aplicável).
 * Appointments são renderizados por cima (ainda não implementado na Fase 1).
 */
export function DayColumn({
  date,
  diaUnico = false,
  startHour,
  endHour,
  slotDurationMinutes,
  gridLines,
  lunchBreak,
  showNowLine,
  nowLineVariant,
  nowLineStyle,
  nowLineColor,
  nowLineHighlightToday,
  isWeekend,
  isInactive,
  appointments,
  laidOutAppointments,
  cardStyle = "colorful",
  showDentistInCard = false,
  dentistSettings = {},
  showTimeInCard = false,
  onAppointmentEdit,
  onAppointmentStatusChange,
  onAppointmentIniciarAtendimento,
  onAppointmentEncerrarAtendimento,
  atendimentoPendente,
  onAppointmentDelete,
  onSlotDoubleClick,
  onAppointmentResize,
  onHover,
  highlightRange,
  compact,
  draggingId,
  deadlineOrders,
  linhasDaFaixa = 0,
  onDeadlineOrderClick,
  sala,
  cabecalho,
}: Props) {
  const slots = buildSlots(startHour, endHour, slotDurationMinutes);
  const isToday = isSameDay(date, new Date());
  const laidOut = laidOutAppointments ?? layoutAppointments(appointments ?? []);

  // Tick de 30s: força re-render pra que `nowMinutesIfToday` passado pros
  // cards fique sempre atualizado, mesmo com aba aberta por horas.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!isToday) return; // outros dias não mudam
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [isToday]);
  const nowMinutesIfToday = isToday
    ? (() => {
        const n = new Date(nowTick);
        return n.getHours() * 60 + n.getMinutes();
      })()
    : null;

  const timeFromEvent = (e: React.MouseEvent<HTMLDivElement>): string | null => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    if (y < 0) return null;
    const minutesFromStart = y / PIXELS_PER_MINUTE;
    const snapMinutes =
      Math.floor(minutesFromStart / slotDurationMinutes) * slotDurationMinutes;
    const slotStart = timeToMinutes(startHour) + snapMinutes;
    const minuteCap = timeToMinutes(endHour) - slotDurationMinutes;
    const clamped = Math.min(Math.max(slotStart, timeToMinutes(startHour)), minuteCap);
    return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSlotDoubleClick) return;
    const time = timeFromEvent(e);
    if (time) onSlotDoubleClick(date, time);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onHover) return;
    const target = e.target as HTMLElement;
    if (target.closest("[data-appointment-card='1']")) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    if (y < 0) {
      onHover(null);
      return;
    }
    const startHourMin = timeToMinutes(startHour);
    const endHourMin = timeToMinutes(endHour);
    const mousePosMin = Math.floor(y / PIXELS_PER_MINUTE) + startHourMin;
    if (mousePosMin < startHourMin || mousePosMin >= endHourMin) {
      onHover(null);
      return;
    }

    // Célula: bloco de `slotDurationMinutes` que contém o mouse (unidade
    // unitária do grid). Dentro da célula, se houver appointment em alguma
    // coluna/fração, destacamos só o espaço livre restante.
    const cellStart =
      Math.floor((mousePosMin - startHourMin) / slotDurationMinutes) *
        slotDurationMinutes +
      startHourMin;
    const cellEnd = Math.min(cellStart + slotDurationMinutes, endHourMin);

    // Fração X do mouse dentro da coluna do dia (0 à esquerda, 1 à direita)
    const mouseX = e.clientX - rect.left;
    const colFrac =
      rect.width > 0
        ? Math.max(0, Math.min(0.9999, mouseX / rect.width))
        : 0;

    // Helpers pra extrair a faixa horizontal [left, right] de um appointment
    const apptXRange = (a: (typeof laidOut)[number]) => {
      const total = a.totalColumns || 1;
      const col = a.column || 0;
      const span = a.colSpan || 1;
      return { left: col / total, right: (col + span) / total };
    };

    // Appointments que interceptam esse slot unitário [cellStart, cellEnd]
    const apptsInCell = laidOut.filter((a) => {
      const s = timeToMinutes(a.startTime);
      const e2 = timeToMinutes(a.endTime);
      return e2 > cellStart && s < cellEnd;
    });

    // Blocos horizontais ocupados no slot (cada appt ocupa [col, col+span])
    const occupied = apptsInCell.map(apptXRange).sort((a, b) => a.left - b.left);

    // Gaps livres em X dentro do slot
    const gaps: Array<{ left: number; right: number }> = [];
    let cursor = 0;
    for (const block of occupied) {
      if (block.left > cursor) gaps.push({ left: cursor, right: block.left });
      cursor = Math.max(cursor, block.right);
    }
    if (cursor < 1) gaps.push({ left: cursor, right: 1 });

    // Qual gap contém o mouse? Se nenhum → slot cheio nessa coluna
    const gap = gaps.find((g) => colFrac >= g.left && colFrac < g.right);
    if (!gap || gap.right - gap.left < 0.01) {
      onHover(null);
      return;
    }

    // Destaque = slot unitário inteiro no tempo + gap livre em X
    onHover({
      date,
      start: minutesToTime(cellStart),
      end: minutesToTime(cellEnd),
      leftFrac: gap.left,
      widthFrac: gap.right - gap.left,
    });
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  const lunchTop = lunchBreak ? positionYFor(lunchBreak.start, startHour) : 0;
  const lunchHeight = lunchBreak
    ? (timeToMinutes(lunchBreak.end) - timeToMinutes(lunchBreak.start)) *
      PIXELS_PER_MINUTE
    : 0;

  return (
    <div
      data-day-column
      data-slot-crosses-now={(() => {
        if (!highlightRange) return undefined;
        const now = new Date();
        if (
          date.getFullYear() !== now.getFullYear() ||
          date.getMonth() !== now.getMonth() ||
          date.getDate() !== now.getDate()
        )
          return undefined;
        const nowMin = now.getHours() * 60 + now.getMinutes();
        const startMin = timeToMinutes(highlightRange.start);
        const endMin = timeToMinutes(highlightRange.end);
        return startMin <= nowMin && nowMin < endMin ? "true" : undefined;
      })()}
      className={`relative min-w-0 flex-1 border-r border-zinc-200 last:border-r-0 dark:border-white/[0.05] ${
        isInactive
          ? "bg-zinc-50/40 dark:bg-black/20"
          : isWeekend
            ? "bg-zinc-50/20 dark:bg-black/10"
            : "bg-white dark:bg-card"
      }`}
    >
      {/* Cabeçalho do dia */}
      <div
        className={`sticky top-0 z-10 flex h-11 items-center justify-center gap-2 border-b bg-white dark:bg-card ${
          isToday
            ? "border-brand-400 dark:border-primary/60"
            : "border-zinc-200 dark:border-white/[0.06]"
        }`}
      >
        {cabecalho ?? (<>
        <span
          className={`${
            compact ? "text-xs" : "text-sm"
          } font-semibold tabular-nums ${
            isToday
              ? `flex ${compact ? "h-6 w-6 text-[11px]" : "h-7 w-7"} items-center justify-center rounded-full bg-brand-600 text-white`
              : "text-zinc-900"
          }`}
        >
          {format(date, "d")}
        </span>
        <span
          className={`font-semibold ${compact ? "text-xs" : "text-sm"} ${
            isToday ? "text-brand-500" : "text-zinc-500"
          }`}
          aria-hidden
        >
          —
        </span>
        <span
          className={`${
            compact ? "text-[10px] uppercase" : "text-sm capitalize"
          } font-semibold truncate ${
            isToday ? "text-brand-700" : "text-zinc-700"
          }`}
        >
          {compact
            ? format(date, "EEE", { locale: ptBR }).replace(".", "")
            : format(date, "EEEE", { locale: ptBR })}
        </span>
        </>)}
      </div>

      {/* Faixa "dia inteiro" — prazo de pedidos de prótese, sem hora, sempre visível ao rolar */}
      {linhasDaFaixa > 0 && (
        <div
          className="sticky top-11 z-10 flex flex-col gap-0.5 overflow-hidden border-b border-zinc-100 bg-white p-0.5 dark:border-white/[0.05] dark:bg-card"
          style={{ height: alturaDaFaixa(linhasDaFaixa) }}
        >
          {(deadlineOrders ?? []).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeadlineOrderClick?.(o.id);
              }}
              title={`Prazo do pedido de ${o.patientName}`}
              style={{ height: ALTURA_DA_LINHA_DA_FAIXA }}
              className={`shrink-0 truncate rounded px-1 text-left text-[10px] font-semibold leading-[18px] ${
                o.overdue
                  ? "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-500/15 dark:text-red-400"
                  : "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-400"
              }`}
            >
              {o.patientName}
            </button>
          ))}
        </div>
      )}

      {/* Grid slots */}
      <div
        className="relative"
        onDoubleClick={handleDoubleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {slots.map((slot) => {
          const top =
            (timeToMinutes(slot) - timeToMinutes(startHour)) * PIXELS_PER_MINUTE;
          const height = slotDurationMinutes * PIXELS_PER_MINUTE;
          const slotMin = timeToMinutes(slot);
          const isInRange =
            !!highlightRange &&
            slotMin >= timeToMinutes(highlightRange.start) &&
            slotMin < timeToMinutes(highlightRange.end);
          return (
            <DroppableSlot
              key={slot}
              date={date}
              time={slot}
              sala={sala}
              top={top}
              height={height}
              gridLines={gridLines}
              isHighlighted={isInRange}
            />
          );
        })}

        {/* Destaque do slot vazio sob o mouse. Cobre só o gap livre (tempo +
            coluna). UM único overlay + pill, zero delay, zero transition. */}
        {highlightRange &&
          (() => {
            const slotTop = positionYFor(highlightRange.start, startHour);
            const slotHeight =
              (timeToMinutes(highlightRange.end) -
                timeToMinutes(highlightRange.start)) *
              PIXELS_PER_MINUTE;
            const leftFrac = highlightRange.leftFrac ?? 0;
            const widthFrac = highlightRange.widthFrac ?? 1;
            return (
              <>
                {/* Fundo leve cobrindo o retângulo livre (X + tempo).
                    z-[25] fica acima da linha vermelha do horário atual (z-20)
                    pra não ter o traço cortando o texto durante o hover. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute z-[25] bg-brand-500/10 ring-1 ring-inset ring-brand-400/40 dark:bg-primary/10 dark:ring-primary/40"
                  style={{
                    top: slotTop,
                    height: slotHeight,
                    left: `calc(${leftFrac * 100}% + 2px)`,
                    width: `calc(${widthFrac * 100}% - 4px)`,
                  }}
                />
                {/* Pill do horário — centralizado no retângulo livre */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute z-[26] inline-flex -translate-x-1/2 -translate-y-1/2 items-center rounded-md bg-brand-600 px-2 py-0.5 text-[10px] font-bold tabular-nums text-white shadow-md dark:bg-primary dark:text-primary-foreground"
                  style={{
                    top: slotTop + slotHeight / 2,
                    left: `${(leftFrac + widthFrac / 2) * 100}%`,
                  }}
                >
                  {highlightRange.start}
                </div>
              </>
            );
          })()}

        {/* Bloqueio de almoço */}
        {lunchBreak && (
          <div
            className="absolute inset-x-0 flex items-center justify-center bg-amber-50/60 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200"
            style={{ top: lunchTop, height: lunchHeight }}
          >
            Almoço
          </div>
        )}

        {/* Appointments (overlay) */}
        {laidOut.map((appt) => (
          <AppointmentCard
            key={appt.id}
            appointment={appt}
            popoverEmbaixo={diaUnico}
            startHour={startHour}
            endHour={endHour}
            slotDurationMinutes={slotDurationMinutes}
            resolvedColor={resolveCardColor(
              { status: appt.status, color: appt.color, patientName: appt.patientName },
              cardStyle,
            )}
            column={appt.column}
            totalColumns={appt.totalColumns}
            colSpan={appt.colSpan}
            dayIsToday={isToday}
            nowMinutesIfToday={nowMinutesIfToday}
            cardStyle={cardStyle}
            showDentist={showDentistInCard}
            dentistTitle={
              appt.dentistId && dentistSettings[appt.dentistId]?.title
                ? dentistSettings[appt.dentistId]?.title
                : "Dr."
            }
            showTime={showTimeInCard}
            isBeingDragged={draggingId === appt.id}
            onEdit={onAppointmentEdit}
            onStatusChange={onAppointmentStatusChange}
            onIniciarAtendimento={onAppointmentIniciarAtendimento}
            onEncerrarAtendimento={onAppointmentEncerrarAtendimento}
            atendimentoPendente={atendimentoPendente}
            onDelete={onAppointmentDelete}
            onResize={onAppointmentResize}
            onHover={onHover}
          />
        ))}

        {/* Linha do horário atual */}
        {showNowLine && (
          <CurrentTimeIndicator
            startHour={startHour}
            endHour={endHour}
            variant={nowLineVariant}
            lineStyle={nowLineStyle}
            color={nowLineColor}
            highlightToday={nowLineHighlightToday}
          />
        )}

        {/* Altura mínima do grid */}
        <div
          style={{
            height:
              (timeToMinutes(endHour) - timeToMinutes(startHour)) *
              PIXELS_PER_MINUTE,
          }}
        />
      </div>
    </div>
  );
}
